/**
 * i18n configuration · v2.7.45+E9 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Stack: react-i18next + i18next + i18next-browser-languagedetector
 *
 * Directory architecture:
 *   src/i18n/
 *     ├── index.js                ← this file (config + init)
 *     ├── useLangSync.js          ← syncs <html lang>/<dir> on locale change
 *     └── locales/
 *         ├── en/
 *         │   ├── common.json     ← buttons, status, errors, generic UI
 *         │   ├── a11y.json       ← screen-reader labels, skip-links
 *         │   ├── admin.json      ← admin console navigation + dashboard
 *         │   ├── email.json      ← Corporate Email module
 *         │   └── signatures.json ← signature builder
 *         ├── es/  ← same 5 namespaces
 *         ├── de/
 *         ├── fr/
 *         ├── zh/
 *         └── ru/
 *
 * Loading strategy: bundled-eager for `common` + `a11y` (always needed)
 * so the first paint is never blocked on a network fetch. The larger
 * domain bundles (admin, email, signatures) are eager-loaded too at
 * present — small JSON (<10KB each) doesn't warrant lazy-loading
 * gymnastics, and react-i18next would otherwise flash the keys to
 * users on first render. If a bundle grows past 50KB we'll switch it
 * to dynamic import().
 *
 * Language priority: localStorage → browser navigator.language → 'en'.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon     from "./locales/en/common.json";
import enA11y       from "./locales/en/a11y.json";
import enAdmin      from "./locales/en/admin.json";
import enEmail      from "./locales/en/email.json";
import enSignatures from "./locales/en/signatures.json";

import esCommon     from "./locales/es/common.json";
import esA11y       from "./locales/es/a11y.json";
import esAdmin      from "./locales/es/admin.json";
import esEmail      from "./locales/es/email.json";
import esSignatures from "./locales/es/signatures.json";

import deCommon     from "./locales/de/common.json";
import deA11y       from "./locales/de/a11y.json";
import deAdmin      from "./locales/de/admin.json";
import deEmail      from "./locales/de/email.json";
import deSignatures from "./locales/de/signatures.json";

import frCommon     from "./locales/fr/common.json";
import frA11y       from "./locales/fr/a11y.json";
import frAdmin      from "./locales/fr/admin.json";
import frEmail      from "./locales/fr/email.json";
import frSignatures from "./locales/fr/signatures.json";

import zhCommon     from "./locales/zh/common.json";
import zhA11y       from "./locales/zh/a11y.json";
import zhAdmin      from "./locales/zh/admin.json";
import zhEmail      from "./locales/zh/email.json";
import zhSignatures from "./locales/zh/signatures.json";

import ruCommon     from "./locales/ru/common.json";
import ruA11y       from "./locales/ru/a11y.json";
import ruAdmin      from "./locales/ru/admin.json";
import ruEmail      from "./locales/ru/email.json";
import ruSignatures from "./locales/ru/signatures.json";

export const SUPPORTED_LANGS = [
  { code: "en", label: "English",  nativeLabel: "English",  flag: "🇺🇸" },
  { code: "es", label: "Spanish",  nativeLabel: "Español",  flag: "🇪🇸" },
  { code: "de", label: "German",   nativeLabel: "Deutsch",  flag: "🇩🇪" },
  { code: "fr", label: "French",   nativeLabel: "Français", flag: "🇫🇷" },
  { code: "zh", label: "Chinese",  nativeLabel: "中文",      flag: "🇨🇳" },
  { code: "ru", label: "Russian",  nativeLabel: "Русский",  flag: "🇷🇺" },
];
export const SUPPORTED_CODES = SUPPORTED_LANGS.map((l) => l.code);

const resources = {
  en: { common: enCommon, a11y: enA11y, admin: enAdmin, email: enEmail, signatures: enSignatures },
  es: { common: esCommon, a11y: esA11y, admin: esAdmin, email: esEmail, signatures: esSignatures },
  de: { common: deCommon, a11y: deA11y, admin: deAdmin, email: deEmail, signatures: deSignatures },
  fr: { common: frCommon, a11y: frA11y, admin: frAdmin, email: frEmail, signatures: frSignatures },
  zh: { common: zhCommon, a11y: zhA11y, admin: zhAdmin, email: zhEmail, signatures: zhSignatures },
  ru: { common: ruCommon, a11y: ruA11y, admin: ruAdmin, email: ruEmail, signatures: ruSignatures },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_CODES,
    nonExplicitSupportedLngs: true,         // accept 'en-US' → 'en'
    ns: ["common", "a11y", "admin", "email", "signatures"],
    defaultNS: "common",
    interpolation: {
      escapeValue: false,                   // React already XSS-escapes
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "rtg-lang",
    },
    react: {
      useSuspense: false,                   // synchronous resources — no Suspense flash
    },
  });

export default i18n;
